import forOwn from 'lodash/forOwn';
import min from 'lodash/min';
import map from 'lodash/map';
import intersection from 'lodash/intersection';
import union from 'lodash/union';
import uniq from 'lodash/uniq';
import forEach from 'lodash/forEach';
import transform from 'lodash/transform';

import {
    PANTS,
    SIZES_ORDER,
    ONE_SIZE,
    MYFILTER_SIZE_CATEGORIES,
    MALE_CAMPAIGN,
    FEMALE_CAMPAIGN,
    UNISEX_CAMPAIGN,
    MALE_CAMPAIGN_PHOENIX,
    FEMALE_CAMPAIGN_PHOENIX,
    UNISEX_CAMPAIGN_PHOENIX,
    TRACK_RECO_NO_RECO,
    TRANSITION_TIME_SCROLL_TOP,
    ARTICLE_MEDIA_TYPE_IMAGE,
    ARITCLE_MEDIA_TYPE_VIDEO,
    ARTICLE_PRICE,
} from '../config/constants';

import { transition, LINEAR } from './transition';

export function timeToArray(millis) {
    const seconds = millis / 1000;
    const m = Math.floor(seconds / 60);
    const h = Math.floor(m / 60);
    const d = Math.floor(h / 24);

    return [d, h % 24, m % 60, Math.floor(seconds % 60)];
}

export function isClient() {
    return !global.__SERVER__ && typeof window !== 'undefined';
}

export function isRetina() {
    return isClient() &&
        window.devicePixelRatio >= 2;
}

export function getPixelRatio() {
    return isClient() && window.devicePixelRatio;
}

export function isTouchScreen() {
    return isClient() &&
        typeof navigator !== undefined &&
        ('ontouchstart' in window || navigator.MaxTouchPoints > 0 || navigator.msMaxTouchPoints > 0);
}

export function isLandscapeAndTouchScreen() {
    return isClient() &&
        isTouchScreen() &&
        window.innerWidth > window.innerHeight;
}

/**
 * Checks in runtime if the given storage is writable.
 * @param {Storage} storage
 * @return {boolean}
 */
const isStorageWritable = storage => {
    const key = '__test_is_enabled';
    const value = String(Math.random());
    try {
        storage.setItem(key, value);
        const res = storage.getItem(key);
        storage.removeItem(key);

        return res === value;
    }
    catch (_) {
        return false;
    }
};

export function isLocalStorageEnabled() {
    if (!isClient()) {
        return false;
    }

    return isStorageWritable(window.localStorage);
}

export function isSessionStorageEnabled() {
    if (!isClient()) {
        return false;
    }

    return isStorageWritable(window.sessionStorage);
}

export function getSessionFlashMessages() {
    let messages = null;

    if (isClient() && window.flashMessagesJson && window.flashMessagesJson.messages) {
        messages = window.flashMessagesJson.messages;
    }

    return messages;
}

export function getCurrencyCode(appDomain) {
    let currencyCode = 'EUR';
    switch (appDomain) {
        case 40:
        case 41:
            currencyCode = 'CHF';
            break;
        case 43:
            currencyCode = 'GBP';
            break;
        case 45:
            currencyCode = 'PLN';
            break;
        case 51:
            currencyCode = 'CZK';
            break;
        default:
            break;
    }

    return currencyCode;
}

export function findLowestPrice(pricesArray, priceType) {
    if (!priceType || !Array.isArray(pricesArray)) {
        return undefined;
    }

    return min(map(pricesArray, priceType));
}

export const findArticleLowestPrice = (article) => findLowestPrice(article.simples, ARTICLE_PRICE);

export function hasDifferentPrices(pricesArray, priceType) {
    if (!priceType || !Array.isArray(pricesArray)) {
        return false;
    }

    return uniq(map(pricesArray, priceType)).length > 1;
}

export function capitalize(string) {
    if (typeof string !== 'string') {
        return '';
    }

    return string.charAt(0).toUpperCase() + string.slice(1);
}

export function flatten(data) {
    const result = {};
    function recurse(cur, prop) {
        if (Object(cur) !== cur) {
            result[prop] = cur;
        }
        else {
            if (Array.isArray(cur)) {
                const len = cur.length;
                for (let i = 0; i < len; i++) {
                    recurse(cur[i], `${prop}[${i}]`);
                }
                if (len === 0) {
                    result[prop] = [];
                }
            }
            else {
                let isEmpty = true;
                for (const p in cur) {
                    isEmpty = false;
                    recurse(cur[p], prop ? `${prop}.${p}` : p);
                }
                if (isEmpty && prop) {
                    result[prop] = {};
                }
            }
        }
    }
    recurse(data, '');

    return result;
}

export function isUpcomingEnabled({ isEnabled }) {
    return isEnabled('upcoming');
}

// analytics, push into dataLayer:
export function track(type, payload) {
    const trackingInfo = { event: type };

    if (typeof payload === 'object') {
        Object.assign(trackingInfo, payload);
    }
    if (typeof payload === 'string') {
        Object.assign(trackingInfo, { data: payload });
    }

    // redux analytics expects a type param, but gtm expects an campaign param, so rename it.
    return isClient() && window.dataLayer && window.dataLayer.push(trackingInfo);
}

export function makeLoopFunction(func, ms) {
    let id;

    return {
        start() {
            id = setInterval(func, ms);
        },
        stop() {
            clearInterval(id);
        }
    };
}

export const safeJsonParse = storedValue => {
    try {
        return JSON.parse(storedValue);
    }
    catch (error) {
        return {};
    }
};

export function storageSaveData(data) {
    const {
        key,
        subKey,
        payload,
        overwrite,
        storageType
    } = data;

    const storage = storageType === 'sessionStorage' ? isSessionStorageEnabled() && sessionStorage : isLocalStorageEnabled() && localStorage;

    if (storage) {
        if (overwrite) {
            const value = safeJsonParse(storage[key]);
            value[subKey] = payload;
            storage[key] = JSON.stringify(value);
        }
        else {
            if (subKey) {
                const value = storage[key] ? JSON.parse(storage[key]) : {};
                value[subKey] = payload;

                storage[key] = JSON.stringify(value);
            }

        }
    }
}

export function isFilterActive(filter) {
    const {
        categories,
        prices,
        sizes,
        colors,
        brands,
        materials
    } = filter || {};

    return !!(filter && Object.keys(filter).length > 0 && (
        (categories && categories.length) ||
        (prices && prices.length) ||
        sizes && SIZES_ORDER.some(group => sizes[group] && sizes[group].length) ||
        (colors && colors.length) || (brands && brands.length) || (materials && materials.length)));
}

export function getUnisexGenderSizes(myFilter) {
    const unisexMyFilter = {};

    forOwn(myFilter.FEMALE, (value, key) => {
        unisexMyFilter[key] = value;
    });

    forOwn(myFilter.MALE, (value, key) => {
        unisexMyFilter[key] = union(unisexMyFilter[key] || [], myFilter.MALE[key]);
    });

    return unisexMyFilter;
}

export function isMyFilterActive(myFilter, availableSizes, gender) {
    const hasMyfilter = myFilter && Object.keys(myFilter).length > 0;
    const hasAvailableSizes = availableSizes && Object.keys(availableSizes).length > 0;

    // If either arguments are not correct, return false immediately
    if (!hasMyfilter || !hasAvailableSizes) {
        return false;
    }

    let genderSpecificMyFilter = {};
    if (gender && gender !== UNISEX_CAMPAIGN) {
        genderSpecificMyFilter = myFilter[gender];
    }
    else {
        // in the case that the category is unisex, or that no gender is provided, apply filter values for both MALE and FEMALE
        genderSpecificMyFilter = getUnisexGenderSizes(myFilter);
    }

    // Then check if any available match the myfilter settings
    const filterContainsAvailable = MYFILTER_SIZE_CATEGORIES.some(group => {
        const myFilterContainsGroup = genderSpecificMyFilter[group] && genderSpecificMyFilter[group].length > 0;
        const availableSizesContainsGroup = availableSizes[group] && availableSizes[group].length > 0;

        return myFilterContainsGroup && availableSizesContainsGroup;
    });

    return filterContainsAvailable;
}

/**
 * This performs correct handling of W and WxL pants sizes
 */
export function mapSizes(sizes, allSizes, applyingMyFilter, gender) {
    const newSizes = {};

    let genderSizes = {};
    if (applyingMyFilter) {
        if (gender === UNISEX_CAMPAIGN) {
            genderSizes = getUnisexGenderSizes(sizes);
        }
        else {
            genderSizes = sizes[gender];
        }
    }
    else {
        genderSizes = sizes;
    }


    if (genderSizes && Object.keys(genderSizes).length) {
        SIZES_ORDER.map((item) => {
            if (allSizes[item] && genderSizes[item] && genderSizes[item].length && item === PANTS) {
                newSizes[item] = allSizes[item].filter((size) => {
                    if (!applyingMyFilter && size.indexOf('x') === -1) {
                        size = size.replace('W', '');
                    }
                    else if (applyingMyFilter) {
                        size = size.replace('W', '').split('x')[0];
                    }
                    if (genderSizes[item].indexOf(size) === -1) {
                        return false;
                    }

                    return true;
                });
            }
            else if (allSizes[item] && genderSizes[item] && genderSizes[item].length) {
                const combinedValues = intersection(allSizes[item], sizes[item]);
                if (combinedValues.length > 0) {
                    newSizes[item] = combinedValues;
                }
                else {
                    newSizes[item] = genderSizes[item];
                }
            }

            return true;
        });
    }

    return newSizes;
}

export function getCategoryGender(genderFilter) {

    if (!genderFilter || genderFilter.length === 0) {
        return UNISEX_CAMPAIGN;
    }

    let gender = '';

    // If there is more than one gender key, it must be a mixed gender category
    if (genderFilter.length > 1 || genderFilter[0] === UNISEX_CAMPAIGN_PHOENIX) {
        gender = UNISEX_CAMPAIGN;
    }
    else if (genderFilter[0] === FEMALE_CAMPAIGN_PHOENIX) {
        gender = FEMALE_CAMPAIGN;
    }
    else if (genderFilter[0] === MALE_CAMPAIGN_PHOENIX) {
        gender = MALE_CAMPAIGN;
    }

    return gender;
}

// can get article or array of simples
export function isOneSizeArticle(item) {
    let { simples } = item;
    if (!simples) {
        simples = item;
    }

    return simples.length === 1 && simples[0].size === ONE_SIZE;
}

export function isSingleSizeArticle(item) {
    const { simples } = item;

    return simples && simples.length === 1 && simples[0].size !== ONE_SIZE;
}

export function isNeighbouringSize(selectedSimpleSku, article, cartItems = []) {
    const { simples = [], configSku } = article;
    const cartSimples = cartItems.filter(item => item.configSku === configSku);
    const simpleSkus = simples.map(simple => simple.sku);
    const selectedSimpleIndex = simpleSkus.indexOf(selectedSimpleSku);

    return !!cartSimples.filter(simple => {
        return (selectedSimpleIndex + 1) === simpleSkus.indexOf(simple.sku) || (selectedSimpleIndex - 1) === simpleSkus.indexOf(simple.sku);
    }).length;
}

export function createBreadCrumb(href, text) {
    return {
        href,
        text
    };
}

export function detectedSwipeDirection({ touchStartX, touchEndX, touchStartY, touchEndY }) {
    let swipeAngle;
    const xDist = touchStartX - touchEndX,
        yDist = touchStartY - touchEndY,
        r = Math.atan2(yDist, xDist);

    swipeAngle = Math.round(r * 180 / Math.PI);
    if (swipeAngle < 0) {
        swipeAngle = 360 - Math.abs(swipeAngle);
    }
    if ((swipeAngle <= 45) && (swipeAngle >= 0) || (swipeAngle <= 360) && (swipeAngle >= 315)) {
        return 'left';
    }
    if ((swipeAngle >= 135) && (swipeAngle <= 225)) {
        return 'right';
    }

    return 'vertical';
}

export function getRecoParameter(state) {
    const { query: urlParams } = state.router.locationBeforeTransitions;
    const additional = {};
    additional.reco = (urlParams && urlParams.reco) || TRACK_RECO_NO_RECO;

    return additional;
}

export function formatDate(dateString) {
    let date = new Date(dateString);

    if (isNaN(date.getDate())) {
        date = new Date(dateString.substring(0, dateString.lastIndexOf('.')));
    }

    return Number(date);
}

export function visibleOnce(payload, callback) {
    let isVisibleOnce = false;

    return function (isVisible) {
        if (!isVisibleOnce && isVisible) {
            isVisibleOnce = true;
            callback(payload);
        }
    };
}

export function getArticleTitles(article, campaign) {
    const {
        brand,
        shop,
        nameShop,
        categoryTag,
        color,
        nameCategoryTag,
        nameColor
    } = article;

    const nameOfShop = shop || nameShop;
    const nameOfCategory = categoryTag || nameCategoryTag;
    const nameOfColor = color || nameColor;
    const appendBrandname = campaign && campaign.options && campaign.options.appendBrandname;

    return {
        brand: appendBrandname && brand,
        shop: !appendBrandname && nameOfShop && nameOfShop,
        category: nameOfCategory,
        color: nameOfColor
    };
}

export function scrollToTop(duration = TRANSITION_TIME_SCROLL_TOP) {
    const body = document.getElementsByTagName('body')[0];
    const html = document.getElementsByTagName('html')[0];

    if (body) {
        transition(body, { scrollTop: 0 }, LINEAR, duration);
    }

    if (html) {
        transition(html, { scrollTop: 0 }, LINEAR, duration);
    }
}

export function isCurrencySymbolOnLeftSide(config) {
    return !!config.symbolOnLeft;
}

export function extractError(...errorLists) {
    if (errorLists.length === 0) {
        return null;
    }

    const totalErrors = errorLists.reduce((result, errors) => result.concat(Array.isArray(errors) ? errors : []), []);

    if (totalErrors.length === 0) {
        return null;
    }

    return {
        error: totalErrors.pop()
    };
}

export function filterCategoryTreeByCategoryIds(allItemsCategory, categoriesIds, returnArrayOfCategories) {
    const firstLevelCategoriesObjects = [];
    const secondLevelCategoriesObjects = [];

    if (!allItemsCategory) {
        return [];
    }

    const firstLevelCategories = allItemsCategory.categories;

    forEach(firstLevelCategories, (item) => {
        if (categoriesIds.indexOf(item.id) > -1) {
            if (returnArrayOfCategories) {
                firstLevelCategoriesObjects.push(item);
            }
            else {
                firstLevelCategoriesObjects.push({
                    category: item
                });
            }
        }
        else {
            forEach(item.categories, (subCategory) => {
                if (categoriesIds.indexOf(subCategory.id) > -1) {
                    if (returnArrayOfCategories) {
                        secondLevelCategoriesObjects.push(subCategory);
                    }
                    else {
                        secondLevelCategoriesObjects.push({
                            parentCategory: item,
                            category: subCategory
                        });
                    }
                }
            });
        }
    });

    return [...firstLevelCategoriesObjects, ...secondLevelCategoriesObjects];
}

export function filterCategoryTreeByCategoryIdsForGlobalCategories(allItemsCategory, categoriesIds, returnArrayOfCategories) {
    if (!allItemsCategory || !(categoriesIds.length)) {
        return [];
    }

    let categoryStack = [allItemsCategory];
    const foundCategories = [];
    let currentParent = null;

    // implement modified DFS algortihm to find selected categories
    while (categoryStack.length) {
        const currentCat = categoryStack.pop();
        if (categoriesIds.includes(currentCat.id)) {
            if (returnArrayOfCategories) {
                foundCategories.push(currentCat);
            }
            else {
                foundCategories.push({
                    category: currentCat,
                    parentCategory: currentParent
                });
            }
        }
        if (currentCat.categories) {
            currentParent = currentCat;
            categoryStack = [...categoryStack, ...currentCat.categories];
        }
    }

    return foundCategories;
}

export function isCategoryTreeContainingOneOf(allItemsCategory, categoriesIds) {
    // This makes the root category be treated as part of the tree.
    if (categoriesIds.indexOf(allItemsCategory.id) !== -1) {
        return true;
    }

    const filteredCategories = filterCategoryTreeByCategoryIdsForGlobalCategories(allItemsCategory, categoriesIds, true);

    return filteredCategories.length > 0;
}

export function resolveReduxThunk(dispatch, getState) {
    return function reduxThunkResolver(thunk) {
        return thunk.call(thunk, dispatch, getState);
    };
}

export function serializeFormData(data) {
    return transform(data, (result, value, key) => result.push(`${key}=${encodeURIComponent(value)}`), []).join('&');
}


export function findSizeBySimple(simples, recommendedSimple = {}) {
    const ssku = recommendedSimple.sku;
    const simple = ssku ? simples.find(item => item.sku === ssku) : null;

    return simple ? simple.size : null;
}

export function isUnisexArticle(article) {
    if (!article) {
        return false;
    }
    const { gender = [], attributes = {} } = article;

    return gender.indexOf('unisex') !== -1 && attributes.is_wearable;
}

export function calcualteAverageRating(reviews) {
    return reviews && reviews.length ? reviews.reduce((acc, review) => acc + review.star_rating, 0) / reviews.length : null;
}

export function getImagesFromMedia(media) {
    return (media || []).filter((medium) => medium.media_type === ARTICLE_MEDIA_TYPE_IMAGE);
}

export function getVideoFromMedia(media) {
    return (media || []).filter((medium) => medium.media_type === ARITCLE_MEDIA_TYPE_VIDEO)[0] || null;
}

export const getArticleUrl = (article) => {
    const categoriesPath = article.categoryId
        ? `/categories/${article.categoryId}`
        : '';

    return `/campaigns/${article.campaignIdentifier}${categoriesPath}/articles/${article.sku}`;
};
